import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Settings as SettingsIcon, Bell, Globe, Moon, Sun, Shield, Trash2,
  ArrowLeft, ChevronRight, Volume2, Smartphone, Info
} from "lucide-react";
import { toast } from "sonner";
import { logOut } from "../lib/firebase";

const Toggle = ({ value, onChange }) => (
  <button
    onClick={() => onChange(!value)}
    className={`relative w-11 h-6 rounded-full transition-colors ${value ? "bg-primary-500" : "bg-slate-200"}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? "translate-x-5" : "translate-x-0"}`}
    />
  </button>
);

const Settings = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({
    notifications:     true,
    chatNotifications: true,
    bookingReminders:  true,
    healthTipAlerts:   false,
    darkMode:          false,
    language:          "English",
    fontSize:          "Medium",
  });

  const set = (key, val) => setPrefs((p) => ({ ...p, [key]: val }));

  const handleDeleteAccount = () => {
    if (window.confirm("Are you sure? This action CANNOT be undone and will permanently delete all your data.")) {
      toast.error("Account deletion is not yet available. Please contact support@supadoc.in.");
    }
  };

  const handleLogout = async () => {
    try {
      await logOut();
      setUser(null);
      navigate("/");
      toast.success("Logged out successfully");
    } catch {
      toast.error("Failed to logout");
    }
  };

  const Section = ({ title, children }) => (
    <div className="glass-card rounded-2xl overflow-hidden mb-4">
      <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-100">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</span>
      </div>
      {children}
    </div>
  );

  const Row = ({ icon: Icon, label, sub, right, onClick, danger }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-0 ${danger ? "text-red-500" : ""}`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${danger ? "bg-red-50" : "bg-primary-50"}`}>
        <Icon className={`w-4.5 h-4.5 ${danger ? "text-red-500" : "text-primary-500"}`} size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${danger ? "text-red-500" : "text-slate-800"}`}>{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
      {right || <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
    </button>
  );

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Header */}
      <div className="glass-panel sticky top-0 z-40 px-4 py-4 border-b border-white/30">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
            <SettingsIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-slate-800">Settings</h1>
            <p className="text-xs text-slate-500">App preferences</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Notifications */}
        <Section title="Notifications">
          <Row
            icon={Bell} label="Push Notifications" sub="General app alerts"
            right={<Toggle value={prefs.notifications} onChange={(v) => { set("notifications", v); toast.success(v ? "Notifications enabled" : "Notifications disabled"); }} />}
          />
          <Row
            icon={Volume2} label="Chat Notifications" sub="New AI response alerts"
            right={<Toggle value={prefs.chatNotifications} onChange={(v) => set("chatNotifications", v)} />}
          />
          <Row
            icon={Bell} label="Booking Reminders" sub="Appointment alerts"
            right={<Toggle value={prefs.bookingReminders} onChange={(v) => set("bookingReminders", v)} />}
          />
          <Row
            icon={Bell} label="Health Tip Alerts" sub="Daily wellness tips"
            right={<Toggle value={prefs.healthTipAlerts} onChange={(v) => set("healthTipAlerts", v)} />}
          />
        </Section>

        {/* Appearance */}
        <Section title="Appearance">
          <Row
            icon={prefs.darkMode ? Moon : Sun}
            label="Dark Mode" sub="Coming soon"
            right={<Toggle value={prefs.darkMode} onChange={() => toast.info("Dark mode coming soon!")} />}
          />
          <Row
            icon={Smartphone} label="Font Size" sub={prefs.fontSize}
            onClick={() => {
              const sizes = ["Small", "Medium", "Large"];
              const next = sizes[(sizes.indexOf(prefs.fontSize) + 1) % sizes.length];
              set("fontSize", next);
              toast.success(`Font size: ${next}`);
            }}
            right={<span className="text-xs text-slate-400 mr-1">{prefs.fontSize}</span>}
          />
        </Section>

        {/* Language */}
        <Section title="Language & Region">
          <Row
            icon={Globe} label="Language" sub={prefs.language}
            onClick={() => toast.info("Multi-language support coming soon!")}
            right={<span className="text-xs text-slate-400 mr-1">{prefs.language}</span>}
          />
        </Section>

        {/* About */}
        <Section title="About">
          <Row icon={Info} label="App Version" sub="SupaDoc AI v1.0.0" onClick={() => {}} right={<span className="text-xs text-slate-400">v1.0.0</span>} />
          <Row icon={Shield} label="Privacy Policy" onClick={() => window.open("https://supadoc.in/privacy", "_blank")} />
          <Row icon={Shield} label="Terms of Service" onClick={() => window.open("https://supadoc.in/terms", "_blank")} />
        </Section>

        {/* Danger zone */}
        <Section title="Account">
          <Row icon={Trash2} label="Delete Account" sub="Permanently delete all your data" onClick={handleDeleteAccount} danger />
        </Section>

        {/* App info footer */}
        <div className="text-center mt-4 pb-4">
          <p className="text-xs text-slate-400">Made with ❤ by SupaDoc Team</p>
          <p className="text-xs text-slate-300 mt-1">© 2025 SupaDoc. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
